import { useEffect, useState, useMemo } from 'react'
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Toolbar,
  Typography,
  Tooltip
} from '@mui/material'
import CommonDataGrid from '@/components/Common/CommonDataGrid'
import { Clear, Delete, Edit, ArrowUpward, ArrowDownward } from '@mui/icons-material'
import toast from 'react-hot-toast'
import PropTypes from 'prop-types'
import {
  getDictionaryDetails,
  addDictionaryDetails,
  updateDictionaryDetails,
  deleteDictionaryDetails
} from '@/api/services/dictionaryServices'
import { IconAddNew, IconReport, IconSave } from '@/assets/assets'
import { runReport } from '@/api/services/printModules'
import { useAuth } from '@/hooks/AuthContext'
import { getAuditFields } from '@/utils/auditUtils'

PackagingManager.propTypes = {
  name: PropTypes.string.isRequired
}

export default function PackagingManager({ name }) {
  const { user } = useAuth()
  const [allData, setAllData] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [newItem, setNewItem] = useState({
    dictionaryCode: name,
    dictionaryValue: '',
    dictionaryValueDescription: '',
    graphTo: '',
    insertDate: new Date().toISOString(),
    updateDate: ''
  })
  const [editOpen, setEditOpen] = useState(false)
  const [currentEdit, setCurrentEdit] = useState(null)

  const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

  const inputLabels = useMemo(
    () => ({
      dictionaryCode: name,
      dictionaryValue: 'Value',
      dictionaryValueDescription: 'Description',
      graphTo: 'Graph To'
    }),
    [name] // Update when name changes
  )

  const fetchData = async () => {
    setLoading(true)
    try {
      const data = await getDictionaryDetails(name)
      if (data && Array.isArray(data)) {
        // Safe Sort: Handle potential null/undefined displayOrder
        const sortedData = [...data].sort(
          (a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0)
        )
        setAllData(sortedData)
        setError(null)
      } else {
        setAllData([])
        setError('No records found.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('Failed to fetch data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setAllData([])
    setNewItem({
      dictionaryCode: name,
      dictionaryValue: '',
      dictionaryValueDescription: '',
      graphTo: '',
      insertDate: new Date().toISOString(),
      updateDate: ''
    })
    setCurrentEdit(null)
    setSearchTerm('')
    setPage(0)
    fetchData()
  }, [name]) // Re-run effect when 'name' changes

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = event => {
    setSearchTerm(event.target.value)
  }

  const filteredData = useMemo(() => {
    return allData.filter(item =>
      item.dictionaryValue.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [allData, searchTerm])

  const handleGenerateReport = async () => {
    try {
      const response = await runReport({
        reportName: 'dictionary',
        parameters: { P_DICTIONARY_CODE: name }
      })

      if (response) {
        const url = URL.createObjectURL(new Blob([response], { type: 'application/pdf' }))
        window.open(url)
      } else {
        toast.error('Failed to generate report.')
      }
    } catch (error) {
      console.error(error)
      toast.error('Error generating report.')
    } finally {
      // setReportOn(false)
    }
  }

  const handleAddItem = async () => {
    if (!newItem.dictionaryValue || newItem.dictionaryValue.trim().length === 0) {
      toast.error('Please fill the Value')
      return
    }
    if (
      !newItem.dictionaryValueDescription ||
      newItem.dictionaryValueDescription.trim().length === 0
    ) {
      toast.error('Please fill the Description')
      return
    }

    // Duplicate check
    const isDuplicate = allData.some(
      item => item.dictionaryValue.toLowerCase() === newItem.dictionaryValue.toLowerCase()
    )
    if (isDuplicate) {
      toast.error(`Duplicate value detected: ${newItem.dictionaryValue}`)
      return
    }

    const disOrder = allData.length + 1
    try {
      const response = await addDictionaryDetails({
        dictionaryCode1: name,
        dictionaryDetailVO: {
          ...newItem,
          displayOrder: disOrder,
          ...getAuditFields(user)
        }
      })
      const createdItem = {
        ...newItem,
        displayOrder: disOrder,
        eaDictionaryDetailId: response.eaDictionaryDetailId
      }
      setAllData(prev => [...prev, createdItem])
      toast.success(`${capitalize(name)} added successfully`)
    } catch (error) {
      console.error('Error in adding the record:', error)
      toast.error('Failed to add the record')
    }
    setOpen(false)
    setNewItem({
      dictionaryCode: name,
      dictionaryValue: '',
      dictionaryValueDescription: '',
      graphTo: '',
      insertDate: new Date().toISOString(),
      updateDate: ''
    })
  }

  const handleEdit = id => {
    const item = allData.find(data => data.eaDictionaryDetailId === id)
    if (item) {
      setCurrentEdit({ ...item })
      setEditOpen(true)
    }
  }

  const handleEditSave = async () => {
    if (!currentEdit.dictionaryValue || currentEdit.dictionaryValue.trim().length === 0) {
      toast.error('Please fill the Value')
      return
    }
    if (
      !currentEdit.dictionaryValueDescription ||
      currentEdit.dictionaryValueDescription.trim().length === 0
    ) {
      toast.error('Please fill the Description')
      return
    }

    // Duplicate check (exclude current item)
    const isDuplicate = allData.some(
      item =>
        item.eaDictionaryDetailId !== currentEdit.eaDictionaryDetailId &&
        item.dictionaryValue.toLowerCase() === currentEdit.dictionaryValue.toLowerCase()
    )
    if (isDuplicate) {
      toast.error(`Duplicate value detected: ${currentEdit.dictionaryValue}`)
      return
    }

    const updatedEdit = {
      ...currentEdit,
      updateDate: new Date().toISOString().replace('Z', '+00:00'),
      appUserId: user?.userId || user?.appUserId || null,
      auditId: user?.auditId || null
    }
    try {
      const { indx: _, ...cleanItem } = updatedEdit
      await updateDictionaryDetails({ dictionaryCode1: name, dictionaryDetailVO: cleanItem })
      setError(null)
      setAllData(prevData =>
        prevData.map(data =>
          data.eaDictionaryDetailId === updatedEdit.eaDictionaryDetailId ? updatedEdit : data
        )
      )
      toast.success(`${capitalize(name)} updated successfully`)
    } catch (error) {
      console.error('Error in updating the record:', error)
      toast.error('Failed to update the record')
    }
    setEditOpen(false)
  }

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      try {
        await deleteDictionaryDetails({
          dictionaryCode1: name,
          dictionaryDetailVO: {
            eaDictionaryDetailId: id,
            ...getAuditFields(user)
          }
        })
        setAllData(prevData => prevData.filter(data => data.eaDictionaryDetailId !== id))
        toast.success(`${capitalize(name)} deleted successfully`)
      } catch (error) {
        console.error('Error in deleting the record:', error)
        toast.error('Failed to delete the record')
      }
    }
  }

  const handleMoveRow = (index, direction) => {
    if (index < 0 || index + direction < 0 || index + direction >= allData.length) return

    setAllData(prevData => {
      const updatedData = [...prevData]
      const [movedItem] = updatedData.splice(index, 1)
      updatedData.splice(index + direction, 0, movedItem)

      return updatedData.map((item, i) => ({
        ...item,
        displayOrder: i + 1,
        updateDate: new Date().toISOString()
      }))
    })
  }

  const saveList = async () => {
    try {
      if (!allData || allData.length === 0) {
        toast.error('No data to save')
        return
      }

      setLoading(true)
      for (const [index, item] of allData.entries()) {
        const { indx: _, ...cleanItem } = item
        const params = {
          dictionaryCode1: name,
          dictionaryDetailVO: {
            ...cleanItem,
            displayOrder: index + 1,
            updateDate: new Date().toISOString(),
            appUserId: user?.userId || user?.appUserId || null,
            auditId: user?.auditId || null
          }
        }
        await updateDictionaryDetails(params)
      }
      toast.success(`${capitalize(name)} list saved successfully`)
      await fetchData()
    } catch (err) {
      console.error('saveList failed:', err)
      const msg = err?.response?.data?.message || err?.message || 'Unknown error'
      toast.error(`Save failed: ${msg}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <AppBar position="sticky" color="primary" sx={{ borderRadius: 1, mb: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h3" style={{ textTransform: 'capitalize' }}>
            {capitalize(name)}
          </Typography>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 20,
              px: 2,
              boxShadow: 2,
              width: '300px'
            }}
          >
            <TextField
              variant="standard"
              placeholder="Search..."
              onChange={handleSearchChange}
              value={searchTerm}
              slotProps={{
                input: {
                  endAdornment: searchTerm && (
                    <IconButton size="small" color="primary" onClick={() => setSearchTerm('')}>
                      <Clear />
                    </IconButton>
                  ),
                  disableUnderline: true
                }
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Add new">
              <Box
                component="img"
                src={IconAddNew}
                alt="Add New"
                sx={{ cursor: 'pointer', width: 32, height: 32 }}
                onClick={() => setOpen(true)}
              />
            </Tooltip>
            <Tooltip title="Save">
              <Box
                component="img"
                src={IconSave}
                alt="Save"
                sx={{ cursor: 'pointer', width: 32, height: 32 }}
                onClick={saveList}
              />
            </Tooltip>
            <Tooltip title="Generate report">
              <Box
                component="img"
                src={IconReport}
                alt="Generate Report"
                sx={{ cursor: 'pointer', width: 32, height: 32 }}
                onClick={handleGenerateReport}
              />
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 2, height: '65vh' }}>
        <CommonDataGrid
          columns={[
            { key: 'dictionaryValue', label: 'Value' },
            { key: 'dictionaryValueDescription', label: 'Description' },
            {
              key: 'actions',
              label: 'Actions',
              render: (data) => {
                const index = allData.findIndex(d => d.eaDictionaryDetailId === data.eaDictionaryDetailId)
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleEdit(data.eaDictionaryDetailId) }}
                      size="small"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleDelete(data.eaDictionaryDetailId) }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleMoveRow(index, -1) }}
                      size="small"
                      disabled={index === 0 || searchTerm !== ''}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleMoveRow(index, 1) }}
                      size="small"
                      disabled={index === allData.length - 1 || searchTerm !== ''}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                )
              }
            }
          ]}
          data={filteredData || []}
          onRowClick={(data) => {}}
          idField="eaDictionaryDetailId"
          rowsPerPage={10}
          height={'100%'}
          loading={loading}
        />
      </Box>

      {/* Add Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add New {capitalize(name)}</DialogTitle>
        <DialogContent>
          <TextField
            required
            margin="normal"
            label="Value"
            fullWidth
            value={newItem.dictionaryValue}
            onChange={e =>
              setNewItem({
                ...newItem,
                dictionaryValue: e.target.value
              })
            }
            sx={{ mb: 2 }}
          />

          {name === 'CONDITIONS' && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setNewItem(prev => ({
                    ...prev,
                    dictionaryValue: (prev.dictionaryValue || '') + '°'
                  }))
                }
              >
                °
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setNewItem(prev => ({
                    ...prev,
                    dictionaryValue: (prev.dictionaryValue || '') + '±'
                  }))
                }
              >
                ±
              </Button>
            </Box>
          )}

          <TextField
            required
            margin="normal"
            label="Description"
            fullWidth
            value={newItem.dictionaryValueDescription}
            onChange={e =>
              setNewItem({
                ...newItem,
                dictionaryValueDescription: e.target.value
              })
            }
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {currentEdit && (
        <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
          <DialogTitle>Edit {capitalize(name)}</DialogTitle>
          <DialogContent>
            {['dictionaryValue', 'dictionaryValueDescription'].map(field => (
              <TextField
                required
                margin="normal"
                key={field}
                label={inputLabels[field]}
                fullWidth
                value={currentEdit[field]}
                onChange={e => setCurrentEdit({ ...currentEdit, [field]: e.target.value })}
                sx={{ mb: 2 }}
              />
            ))}

            {name === 'CONDITIONS' && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setCurrentEdit(prev => ({
                      ...prev,
                      dictionaryValue: (prev.dictionaryValue || '') + '°'
                    }))
                  }
                >
                  °
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setCurrentEdit(prev => ({
                      ...prev,
                      dictionaryValue: (prev.dictionaryValue || '') + '±'
                    }))
                  }
                >
                  ±
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}
